import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import YouTube from 'react-youtube';
import { Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const YOUTUBE_OPTS = {
  width: '100%',
  height: '100%',
  playerVars: { autoplay: 0 },
};
const YOUTUBE_ID_REGEX = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

const VIDEO_CATEGORIES = [
  { id: 'all', label: 'Toutes' },
  { id: 'temoignage', label: 'Témoignages' },
  { id: 'activite', label: 'Activités' },
  { id: 'formation', label: 'Formations FPT' },
  { id: 'evenement', label: 'Événements' },
];

const CAT_LABEL = {
  temoignage: 'Témoignage',
  activite: 'Activité',
  formation: 'Formation',
  evenement: 'Événement',
  autre: 'Autre',
};

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(YOUTUBE_ID_REGEX);
  return (match && match[2].length === 11) ? match[2] : null;
};

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCat, setActiveCat] = useState('all');

  const fetchVideos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
      if (response.data.length > 0) {
        setSelectedVideo(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const filteredVideos = useMemo(() => {
    if (activeCat === 'all') return videos;
    return videos.filter((v) => (v.category || 'activite') === activeCat);
  }, [videos, activeCat]);

  // Reset the featured video when the filter no longer contains it
  useEffect(() => {
    if (filteredVideos.length === 0) return;
    if (!selectedVideo || !filteredVideos.some((v) => v.video_id === selectedVideo.video_id)) {
      setSelectedVideo(filteredVideos[0]);
    }
  }, [filteredVideos, selectedVideo]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="videos-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="videos-title"
            >
              Galerie Vidéos
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Découvrez nos activités en images et vidéos.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}

          {!loading && videos.length === 0 && (
            <div className="text-center py-12" data-testid="no-videos-message">
              <p className="text-[#5C665A]">Aucune vidéo disponible pour le moment.</p>
            </div>
          )}

          {!loading && videos.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {VIDEO_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  data-testid={`video-cat-${c.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                    activeCat === c.id
                      ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                      : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {!loading && filteredVideos.length === 0 && videos.length > 0 && (
            <div className="text-center py-8" data-testid="no-videos-filter-message">
              <p className="text-[#5C665A]">Aucune vidéo dans cette catégorie.</p>
            </div>
          )}

          {!loading && filteredVideos.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Featured Video */}
              <div className="lg:col-span-2" data-testid="featured-video">
                {selectedVideo && selectedVideo.youtube_url && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <YouTube
                      videoId={extractYouTubeId(selectedVideo.youtube_url)}
                      opts={YOUTUBE_OPTS}
                      className="w-full h-full"
                    />
                  </div>
                )}
                {selectedVideo && !selectedVideo.youtube_url && selectedVideo.external_url && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      controls
                      playsInline
                      preload="metadata"
                      controlsList="nodownload noremoteplayback"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full"
                      src={selectedVideo.external_url}
                    >
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>
                )}
                {selectedVideo && !selectedVideo.youtube_url && !selectedVideo.external_url && selectedVideo.storage_path && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      controls
                      playsInline
                      preload="metadata"
                      controlsList="nodownload noremoteplayback"
                      onContextMenu={(e) => e.preventDefault()}
                      className="w-full h-full"
                      src={`${API}/files/${selectedVideo.storage_path}`}
                    >
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>
                )}
                
                {selectedVideo && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {selectedVideo.category && (
                        <span className="text-xs font-bold px-2 py-1 rounded bg-[#4A6B48]/10 text-[#4A6B48]">
                          {CAT_LABEL[selectedVideo.category] || 'Activité'}
                        </span>
                      )}
                    </div>
                    <h2 
                      className="text-2xl font-bold text-[#2E332D] mb-2"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {selectedVideo.title}
                    </h2>
                    <p className="text-sm text-[#5C665A] leading-relaxed">
                      {selectedVideo.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Video List */}
              <div className="space-y-4" data-testid="video-list">
                {filteredVideos.map((video) => (
                  <div
                    key={video.video_id}
                    onClick={() => setSelectedVideo(video)}
                    data-testid={`video-item-${video.video_id}`}
                    className={`card cursor-pointer transition-all ${
                      selectedVideo?.video_id === video.video_id 
                        ? 'ring-2 ring-[#4A6B48]' 
                        : 'hover:shadow-lg'
                    }`}
                  >
                    <div className="relative">
                      {video.thumbnail_url ? (
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title}
                          className="w-full h-24 object-cover rounded mb-3"
                        />
                      ) : (
                        <div className="w-full h-24 bg-[#4A6B48]/10 rounded mb-3 flex items-center justify-center">
                          <Play className="text-[#4A6B48]" size={32} />
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-[#2E332D] line-clamp-2">
                      {video.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
